import {
  findCompetitiveElo,
  parseBattleLog,
  parsePreamble,
  splitExport,
} from "@pra/shared/battle-log";
import type { UploadGameResponse } from "@pra/shared/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { ErrorBox, PageHead } from "../components/Bits";
import { Badge, Button, Card, Input, Select } from "../design-system/components";
import { endpoints } from "../lib/api";
import { useSession } from "../lib/session";

type Pending = { text: string; fileName?: string; playedAt?: string };

export function UploadPage() {
  const client = useQueryClient();
  const { profile } = useSession();
  const decks = useQuery({ queryKey: ["decks"], queryFn: endpoints.decks });
  const [pending, setPending] = useState<Pending>({ text: "" });
  const [over, setOver] = useState(false);
  const [side, setSide] = useState<string>("");
  const [deckChoice, setDeckChoice] = useState<string>("");
  const [newDeckName, setNewDeckName] = useState("");
  const [results, setResults] = useState<UploadGameResponse[]>([]);
  const [queue, setQueue] = useState<Pending[]>([]);

  const preview = useMemo(() => {
    if (pending.text.trim().length < 20) return undefined;
    try {
      const split = splitExport(pending.text);
      const parsed = parseBattleLog(split.battleLogText);
      const preamble = parsePreamble(split.preambleText);
      const handles = profile?.tcglHandles ?? [];
      let detected: number | undefined;
      let source = "";
      if (preamble.local) {
        detected = parsed.players.indexOf(preamble.local.playerName);
        source = "debug preamble";
      } else if (parsed.mySideHint) {
        detected = parsed.mySideHint.side;
        source = parsed.mySideHint.source === "concede" ? "concede line" : "named draws";
      } else {
        const idx = parsed.players.findIndex((p) =>
          handles.some((h) => h.toLowerCase() === p.toLowerCase()),
        );
        if (idx >= 0) {
          detected = idx;
          source = "your handle";
        }
      }
      const elo = findCompetitiveElo(split.trailerLines);
      return { split, parsed, preamble, detected, source, elo };
    } catch {
      return undefined;
    }
  }, [pending.text, profile]);

  const upload = useMutation({
    mutationFn: async (
      item: Pending & { mySide?: number; deckId?: string; newDeckName?: string },
    ) =>
      endpoints.uploadGame({
        text: item.text,
        ...(item.mySide !== undefined ? { mySide: item.mySide } : {}),
        ...(item.deckId ? { deckId: item.deckId } : {}),
        ...(item.newDeckName ? { newDeckName: item.newDeckName } : {}),
        ...(item.fileName ? { fileName: item.fileName } : {}),
        ...(item.playedAt ? { playedAt: item.playedAt } : {}),
      }),
    onSuccess: (res) => {
      setResults((r) => [res, ...r]);
      void client.invalidateQueries();
      const [next, ...rest] = queue;
      setQueue(rest);
      setPending(next ?? { text: "" });
      setSide("");
    },
  });

  const readFiles = async (files: FileList | File[]) => {
    const list = await Promise.all(
      [...files].map(async (f) => ({
        text: await f.text(),
        fileName: f.name,
        playedAt: new Date(f.lastModified).toISOString(),
      })),
    );
    const [first, ...rest] = list;
    if (first) setPending(first);
    setQueue((q) => [...q, ...rest]);
  };

  const effectiveSide = side !== "" ? Number.parseInt(side, 10) : preview?.detected;
  const needsSide = preview !== undefined && effectiveSide === undefined;
  const hasDebugDeck = preview?.preamble.local?.meta.tcglDeckId !== undefined;
  const submit = () =>
    upload.mutate({
      ...pending,
      ...(effectiveSide !== undefined && effectiveSide >= 0 ? { mySide: effectiveSide } : {}),
      ...(deckChoice && deckChoice !== "__new" ? { deckId: deckChoice } : {}),
      ...(deckChoice === "__new" && newDeckName ? { newDeckName } : {}),
    });

  return (
    <div className="rk-stack">
      <PageHead
        title="Upload a battle log"
        subtitle="Paste the text from TCG Live's Battle Log → Export, or drop match-export files from the debug client."
      />
      <div className="rk-split rk-split--wide">
        <Card title="Log text">
          <section
            className="pr-drop"
            aria-label="Drop battle log files"
            data-over={over}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              void readFiles(e.dataTransfer.files);
            }}
          >
            Drop <code>match-export-*.txt</code> files here, or{" "}
            <label style={{ color: "var(--primary)", cursor: "pointer" }}>
              choose files
              <input
                type="file"
                multiple
                accept=".txt,text/plain"
                style={{ display: "none" }}
                onChange={(e) => e.target.files && void readFiles(e.target.files)}
              />
            </label>
            {queue.length > 0 && <div style={{ marginTop: 8 }}>{queue.length} more queued</div>}
          </section>
          <div style={{ height: 12 }} />
          <Input
            as="textarea"
            rows={14}
            placeholder={"Setup\nSigma_E chose tails for the opening coin flip.\n…"}
            value={pending.text}
            onChange={(e) => setPending({ text: e.target.value })}
            style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}
          />
        </Card>
        <Card title="Preview" description="What we found before it's saved.">
          {!preview ? (
            <p className="pr-muted" style={{ fontSize: 14 }}>
              Paste or drop a log to see the players, result and detected side.
            </p>
          ) : (
            <div className="rk-stack" style={{ gap: 14 }}>
              <div className="pr-inline">
                <Badge variant={preview.split.variant === "debug" ? "accent" : "outline"}>
                  {preview.split.variant} export
                </Badge>
                <Badge variant="outline">{preview.parsed.turnCount} turns</Badge>
                {preview.parsed.unparsedCount > 0 && (
                  <Badge variant="warning">{preview.parsed.unparsedCount} unparsed lines</Badge>
                )}
              </div>
              <dl className="pr-kv">
                <dt>Players</dt>
                <dd>{preview.parsed.players.join(" vs ")}</dd>
                <dt>Winner</dt>
                <dd>{preview.parsed.winner ?? "unknown"}</dd>
                <dt>Your side</dt>
                <dd>
                  {preview.detected !== undefined && preview.detected >= 0
                    ? `${preview.parsed.players[preview.detected]} (${preview.source})`
                    : "not detected — pick below"}
                </dd>
                {preview.preamble.local && (
                  <>
                    <dt>Deck</dt>
                    <dd>
                      {preview.preamble.local.meta.deckName} ·{" "}
                      {preview.preamble.local.cards.reduce((n, c) => n + c.count, 0)} cards
                    </dd>
                  </>
                )}
                {preview.elo && (
                  <>
                    <dt>Elo</dt>
                    <dd>
                      {preview.elo.previousElo} → {preview.elo.newElo} (
                      {preview.elo.delta >= 0 ? "+" : ""}
                      {preview.elo.delta}) ·{" "}
                      {preview.elo.seasonName ?? `season ${preview.elo.seasonId}`}
                    </dd>
                  </>
                )}
                {preview.preamble.inferredPrizes && (
                  <>
                    <dt>Prized</dt>
                    <dd>{preview.preamble.inferredPrizes.length} cards inferred</dd>
                  </>
                )}
              </dl>
              {(needsSide || side !== "") && (
                <Select
                  label="Which player are you?"
                  value={side}
                  onChange={setSide}
                  placeholder="Choose your side"
                  options={preview.parsed.players.map((p, i) => ({ value: String(i), label: p }))}
                />
              )}
              {needsSide && side === "" && (
                <p className="pr-help">
                  We couldn't tell which side is yours. Pick it so results and stats are attributed
                  correctly.
                </p>
              )}
              {!hasDebugDeck && (
                <>
                  <Select
                    label="Deck"
                    value={deckChoice}
                    onChange={setDeckChoice}
                    placeholder="Link to a deck (optional)"
                    options={[
                      ...(decks.data?.decks ?? [])
                        .filter((d) => !d.archived)
                        .map((d) => ({ value: d.deckId, label: d.name })),
                      { value: "__new", label: "+ New deck…" },
                    ]}
                  />
                  {deckChoice === "__new" && (
                    <Input
                      label="New deck name"
                      value={newDeckName}
                      onChange={(e) => setNewDeckName(e.target.value)}
                    />
                  )}
                </>
              )}
              {upload.error && <ErrorBox error={upload.error} />}
              <Button
                onClick={submit}
                disabled={upload.isPending || (needsSide && side === "")}
                fullWidth
              >
                {upload.isPending ? "Uploading…" : "Save game"}
              </Button>
            </div>
          )}
        </Card>
      </div>
      {results.length > 0 && (
        <Card title="Uploaded this session">
          <ul className="pr-list">
            {results.map((r) => (
              <li key={r.game.gameId}>
                <span>
                  <Link to={`/games/${r.game.gameId}`}>
                    {r.game.players.join(" vs ")} · {r.game.result}
                  </Link>{" "}
                  {r.duplicate && <Badge variant="warning">already uploaded</Badge>}{" "}
                  {r.needsSide && <Badge variant="warning">side unknown</Badge>}{" "}
                  {r.needsDeck && <Badge variant="outline">no deck</Badge>}
                </span>
                <span>{r.game.deckName ?? ""}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
